import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react'
import { Box } from '@mui/material'
import { Highlight } from '@tiptap/extension-highlight'
import { Image as TiptapImage } from '@tiptap/extension-image'
import { Link } from '@tiptap/extension-link'
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table'
import { TextAlign } from '@tiptap/extension-text-align'
import {
  BackgroundColor,
  Color,
  FontFamily,
  FontSize,
  TextStyle,
} from '@tiptap/extension-text-style'
import { Underline } from '@tiptap/extension-underline'
import { type AnyExtension, type Editor, EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'

import { proseLinkStyles } from '~components/internal/styles/links'
import { BASE_URL_WITH_ALL_REACT } from '~config'
import { borderRadius, colors, lightTokens, spacing, typography } from '~tokens/internal'

import { EmailAIReplyBody, EmailFooter, EmailHeader } from './emailEditorExtensions'
import './RichTextEditor.scss'
import RichTextEditorToolbar from './RichTextEditorToolbar'

export const PROSE_CONTENT_STYLES = {
  fontFamily: 'avenir-roman, sans-serif',
  wordWrap: 'break-word' as const,
  overflowWrap: 'break-word' as const,
  '& p': {
    marginTop: 0,
    marginBottom: spacing.xxs,
    lineHeight: spacing.sm,
    minHeight: spacing.sm,
  },
  '& p:last-child': { marginBottom: 0, minHeight: 0 },
  '& pre': { whiteSpace: 'pre-wrap' as const },
  '& ul, & ol': {
    paddingLeft: spacing.sm,
    listStylePosition: 'outside' as const,
    marginBottom: spacing.compact,
  },
  '& li': { marginBottom: spacing.micro },
  '& li > p:empty::before': { content: '"\\00a0"' },
  '& a': { ...proseLinkStyles, color: lightTokens.secondary.main },
  '& img': { maxWidth: '100%', height: 'auto' },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    '& td, & th': {
      border: `1px solid ${lightTokens.action.disabled}`,
      padding: spacing.xxs,
      minWidth: '50px',
    },
    '& th': { backgroundColor: lightTokens.action.disabledBackground, fontWeight: 'bold' },
  },
  '& blockquote': {
    borderLeft: `3px solid ${lightTokens.action.disabled}`,
    paddingLeft: spacing.compact,
    marginLeft: 0,
    color: lightTokens.text.secondary,
  },
  '& .email-header': {
    userSelect: 'none',
    cursor: 'default',
    '& .email-header-logo': { width: '85px', height: 'auto', marginBottom: spacing.compact },
    '& .email-header-name': {
      fontSize: typography.fontSize.xxl,
      lineHeight: typography.lineHeight.tight,
      fontFamily: 'avenir-book, sans-serif',
      color: lightTokens.text.primary,
      marginTop: '0',
      marginBottom: spacing.compact,
      fontWeight: 'normal',
    },
  },
  '& .email-divider': {
    border: 'none',
    borderTop: `1px solid ${lightTokens.text.secondary}`,
    margin: `0 0 ${spacing.sm} 0`,
  },
  '& .email-footer': {
    userSelect: 'none',
    cursor: 'default',
    '& .email-divider': { margin: `${spacing.xl} 0 ${spacing.sm} 0` },
    '& .email-footer-content': { display: 'flex', alignItems: 'center', gap: spacing.micro },
    '& .email-footer-icon': {
      width: typography.fontSize.lg,
      height: typography.fontSize.lg,
      objectFit: 'contain',
    },
    '& .email-footer-text': {
      fontSize: typography.fontSize.xxs,
      color: lightTokens.text.secondary,
      fontFamily: 'avenir-book, sans-serif',
      lineHeight: spacing.sm,
    },
  },
  '& .email-ai-reply-body': {
    userSelect: 'none',
    cursor: 'default',
    border: `1px dashed ${lightTokens.secondary.main}`,
    borderRadius: borderRadius.sm,
    padding: spacing.xxs,
    marginBottom: spacing.xxs,
    color: lightTokens.text.secondary,
    fontSize: typography.fontSize.xs,
  },
  '& .mention': {
    backgroundColor: colors.neutral[100],
    color: lightTokens.text.primary,
    borderRadius: borderRadius.full,
    padding: `${spacing.pico} ${spacing.micro}`,
    fontWeight: typography.fontWeight.medium,
    fontSize: typography.fontSize.xs,
    lineHeight: typography.lineHeight.tight,
  },
} as const

export const CustomImage = TiptapImage.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (element: HTMLElement): string | null =>
          element.getAttribute('width') || element.style.width || null,
        renderHTML: (attributes: Record<string, string>): Record<string, string> => {
          if (!attributes.width) return {}
          return { width: attributes.width }
        },
      },
      height: {
        default: null,
        parseHTML: (element: HTMLElement): string | null =>
          element.getAttribute('height') || element.style.height || null,
        renderHTML: (attributes: Record<string, string>): Record<string, string> => {
          if (!attributes.height) return {}
          return { height: attributes.height }
        },
      },
      style: {
        default: null,
        parseHTML: (element: HTMLElement): string | null => element.getAttribute('style') || null,
        renderHTML: (attributes: Record<string, string>): Record<string, string> => {
          const parts: string[] = []

          if (attributes.width) {
            const w = String(attributes.width)
            parts.push(`width: ${/^\d+$/.test(w) ? w + 'px' : w}`)
          }
          if (attributes.height) {
            const h = String(attributes.height)
            parts.push(`height: ${/^\d+$/.test(h) ? h + 'px' : h}`)
          }

          if (attributes.style) {
            const cleaned = String(attributes.style)
              .replace(/\bwidth\s*:[^;]+(;|$)/gi, '')
              .replace(/\bheight\s*:[^;]+(;|$)/gi, '')
              .replace(/;\s*$/, '')
              .trim()
            if (cleaned) parts.push(cleaned)
          }

          if (parts.length === 0) return {}
          return { style: parts.join('; ') }
        },
      },
    }
  },
})

interface RichTextEditorProps {
  initialContent: string
  onChange?: (htmlContent: string) => void
  className?: string
  isTemplateEditor?: boolean
  iconSize?: number
  extensions?: AnyExtension[]
  height?: number
  maxHeight?: number
}

export interface RichTextEditorRef {
  getContent: () => string
  setContent: (content: string) => void
  insertHTML: (html: string) => void
  getSelectedText: () => string
}

const templateLinks = [
  {
    text: 'Case Link',
    href: `${BASE_URL_WITH_ALL_REACT}/cases/{{$CASE_ID}}/apply`,
  },
  {
    text: 'Internal Case Link',
    href: `${BASE_URL_WITH_ALL_REACT}/cases/{{$CASE_ID}}`,
  },
]

const RichTextEditorComponent = forwardRef<RichTextEditorRef, RichTextEditorProps>(
  (
    {
      initialContent,
      onChange,
      className,
      isTemplateEditor = false,
      iconSize,
      extensions: extraExtensions = [],
      height,
      maxHeight,
    },
    ref,
  ) => {
    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange
    const lastContentRef = useRef(initialContent || '')

    const handleUpdate = useCallback(({ editor }: { editor: Editor }) => {
      const html = editor.getHTML()
      lastContentRef.current = html
      if (onChangeRef.current) {
        onChangeRef.current(html)
      }
    }, [])

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: { levels: [1, 2, 3] },
        }),
        Underline,
        TextStyle,
        Color,
        FontSize,
        FontFamily,
        BackgroundColor,
        Highlight.configure({ multicolor: true }),
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
        Link.configure({
          openOnClick: false,
          autolink: true,
          defaultProtocol: 'https',
          HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
        }),
        CustomImage.configure({
          inline: true,
          allowBase64: false,
        }),
        Table.configure({ resizable: true }),
        TableRow,
        TableHeader,
        TableCell,
        ...(isTemplateEditor ? [EmailHeader, EmailFooter, EmailAIReplyBody] : []),
        ...extraExtensions,
      ],
      content: initialContent || '',
      onUpdate: handleUpdate,
    })

    useImperativeHandle(ref, () => ({
      getContent: (): string => {
        return editor?.getHTML() || ''
      },
      setContent: (content: string): void => {
        if (editor) {
          lastContentRef.current = content
          editor.commands.setContent(content)
        }
      },
      insertHTML: (html: string): void => {
        if (editor) {
          editor.chain().focus().insertContent(html).run()
        }
      },
      getSelectedText: (): string => {
        if (!editor) return ''
        const { from, to } = editor.state.selection
        return editor.state.doc.textBetween(from, to, ' ')
      },
    }))

    useEffect(() => {
      if (editor && initialContent !== lastContentRef.current) {
        lastContentRef.current = initialContent
        editor.commands.setContent(initialContent)
      }
    }, [initialContent, editor])

    const toolbarIconSize = iconSize
      ? `clamp(${typography.fontSize.xxs}, ${iconSize}px, ${typography.fontSize.lg})`
      : isTemplateEditor
        ? typography.fontSize.sm
        : typography.fontSize.xs

    return (
      <Box
        className={`rich-text-editor${className ? ` ${className}` : ''}`}
        tabIndex={0}
        sx={
          height !== undefined || maxHeight !== undefined
            ? {
                '& .tiptap-editor .ProseMirror': {
                  ...(height !== undefined ? { minHeight: `${height}px` } : {}),
                  ...(maxHeight !== undefined ? { maxHeight: `${maxHeight}px` } : {}),
                },
              }
            : undefined
        }
      >
        {editor && (
          <RichTextEditorToolbar
            editor={editor}
            isTemplateEditor={isTemplateEditor}
            templateLinks={templateLinks}
            iconSize={toolbarIconSize}
          />
        )}
        <Box className='tiptap-editor' sx={PROSE_CONTENT_STYLES}>
          <EditorContent editor={editor} />
        </Box>
      </Box>
    )
  },
)

export default RichTextEditorComponent
